// © Microsoft Corporation. All rights reserved.

import { endsWith as _endsWith, some as _some } from "lodash-es";

import { IActionMap } from "./IActionMap";
import { IActionPayload } from "./IActionPayload";
import { String as StringOperations } from "typescript-string-operations";

/**
 * Class Contains helpers to use inside of a reducer, that is managed by and called from DynamicReducer
 * These helpers provide the ability to use a strongly typed callback that matches the parameters of an Action.
 * redux-subspace will automatically prepend a namespace to actions so that they are targeted to their specific subspace.
 * DynamicReducer already handles ensuring actions reach the correct reducer, so the redux-subspace namespace can be ignored
 * @class
 */
export class ActionHandler {

    /**
     * local function that either uses the built-in String.prototype.endsWith function, or uses the lodash-es function to polyFill;
     * the built-in function is significantly faster than the lodash-es function or other options, so we want to use it when we can;
     * the lodash-es function, based on splice, is faster than String.prototype.indexOf, but using RegEx might be faster...
     */
    private static endsWith: Function = String.prototype.endsWith
        ? function (search: string, target: string): boolean { return search.endsWith(target); }
        : _endsWith;

    /**
     * Method to handle specific actions.
     * @method
     * @param actionPayload {IActionPayload} The action details.
     * @param actionMap {IActionMap} The actionMap which maps Action to its callback function type.
     * @param callback {TActionSignature} function of type TActionSignature.
     * @return { boolean } true if the action type matched, false otherwise.
     */
    public static handleAction<TActionSignature extends Function>(
        actionPayload: IActionPayload,
        actionMap: IActionMap<TActionSignature>,
        callback: TActionSignature): boolean {

        // actionPayload.type may have a redux-subspace namespace prepended, so check to see if the ending action is what we want
        // namespaces are separated from the action type by a "/";
        // we use that to differentiate where the payload type ends with the mapped type as part of the name, but is not the same action
        // e.g. "Action" in the action map and "NameSpace/AnotherAction" in the payload, both end in "Action" but do not match
        // we prepend to *both* to cover the case where they are already equal (that is, no namespace is present)

        if (ActionHandler.endsWith(actionPayload.type + "", actionMap.type)) {
            callback.apply(callback, actionPayload.params);
            return true;
        } else {
            return false;
        }
    }

    /**
     * Method to handle multiple actions with same signature.
     * @method
     * @param actionPayload {IActionPayload} The action details.
     * @param actionMap {IActionMap<TActionSignature>[]} The collection of actionMaps which maps Action to its callback function type.
     * @param callback {TActionSignature} function of type TActionSignature.
     * @return { boolean } true if the action type matched, false otherwise.
     */
    public static handleActions<TActionSignature extends Function>(
        actionPayload: IActionPayload,
        actionMaps: IActionMap<TActionSignature>[],
        callback: TActionSignature): boolean {

        const actionPayloadType = actionPayload.type + "";
        const actionsMatched = _some(actionMaps, (actionMap) => ActionHandler.endsWith(actionPayloadType, actionMap.type));

        if (actionsMatched) {
            callback.apply(callback, actionPayload.params);
            return true;
        } else {
            return false;
        }
    }

    /**
     * Method to handle specific actions and check typescript type safely.
     * It throws error if actionMap.type does not equal the actionPayload.type.
     * @method
     * @param actionPayload {IActionPayload} The action details.
     * @param actionMap {IActionMap} The actionMap which maps Action to its callback function type.
     * @param callback {TActionSignature} function of type TActionSignature.
     * @return { boolean } true if the action type matched, false otherwise.
     */
    public static safeHandleAction<TActionSignature extends Function>(
        actionPayload: IActionPayload,
        actionMap: IActionMap<TActionSignature>,
        callback: TActionSignature): any {

        if (!ActionHandler.endsWith(actionPayload.type + "", actionMap.type)) {
            throw "The action payload does not match the action type.";
        }

        callback.apply(callback, actionPayload.params);
        return true;
    }
}